// "Subscribe to my jobs" card. Reveals a private calendar feed link the
// user adds to their phone once; jobs then sync automatically. Owners get
// every scheduled job (or only assigned ones, via the toggle) plus
// optional per-guard feeds for color-coding; guards get only the ones
// they're assigned to.
import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebase";
import {
  ensureCalendarToken, regenerateCalendarToken, calendarSubscribeUrls,
} from "../services/calendarSubscribe";

export default function CalendarSubscribe() {
  const { userProfile } = useAuth();
  const [token, setToken] = useState(userProfile?.calendarToken || "");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState("");
  const isOwner = userProfile?.role === "owner";
  // Owner feed scope: all company jobs vs only the jobs the owner is
  // personally assigned to (owners ride along as guards sometimes).
  // Baked into the subscribe URL, so switching after subscribing means
  // re-subscribing with the new link.
  const [mineOnly, setMineOnly] = useState(false);
  // Per-guard color-coding: calendar apps ignore per-event colors in
  // subscribed feeds, so each guard gets their OWN feed URL — subscribe
  // to each and give the calendar a color in Google/Apple Calendar.
  const [showGuardFeeds, setShowGuardFeeds] = useState(false);
  const [guards, setGuards] = useState([]);
  const [copiedGuardId, setCopiedGuardId] = useState("");
  useEffect(() => {
    if (!isOwner || !userProfile?.companyId) return;
    getDocs(query(
      collection(db, "users"),
      where("companyId", "==", userProfile.companyId),
      where("role", "==", "guard"),
    )).then((snap) => {
      setGuards(snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""))));
    }).catch((e) => console.warn("Couldn't load guards for calendar feeds", e));
  }, [isOwner, userProfile?.companyId]);

  async function reveal() {
    if (!userProfile?.id) return;
    setBusy(true); setErr("");
    try {
      setToken(await ensureCalendarToken(userProfile.id, userProfile.calendarToken));
    } catch (e) { setErr(e?.message || "Couldn't set up the calendar link."); }
    setBusy(false);
  }
  async function reset() {
    if (!userProfile?.id) return;
    if (!window.confirm("Reset the calendar link? The old link stops working and you'll need to re-subscribe on each device.")) return;
    setBusy(true); setErr("");
    try { setToken(await regenerateCalendarToken(userProfile.id)); setCopied(false); }
    catch (e) { setErr(e?.message || "Couldn't reset the link."); }
    setBusy(false);
  }

  const urls = token ? calendarSubscribeUrls(token, { mineOnly: isOwner && mineOnly }) : null;
  async function copy() {
    if (!urls) return;
    try { await navigator.clipboard.writeText(urls.https); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    catch { setErr("Copy failed — long-press the link to copy it."); }
  }
  async function copyGuardLink(guardId) {
    try {
      await navigator.clipboard.writeText(calendarSubscribeUrls(token, { guardId }).https);
      setCopiedGuardId(guardId);
      setTimeout(() => setCopiedGuardId(""), 2000);
    } catch { setErr("Copy failed — long-press the link to copy it."); }
  }

  const scope = isOwner
    ? (mineOnly ? "only the jobs you're assigned to" : "every scheduled job")
    : "the jobs you're assigned to";

  return (
    <div style={{ border: "1px solid #e8e8e8", borderRadius: 10, padding: 14, background: "#fff" }}>
      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>📅 Subscribe to your job calendar</div>
      <div style={{ fontSize: 12, color: "#666", marginBottom: 10 }}>
        Adds {scope} to your phone's calendar and keeps them in sync automatically — new jobs appear, changes update.
      </div>

      {err && (
        <div style={{ background: "#fff0f0", border: "1px solid #f5a5a5", color: "#a32d2d", borderRadius: 6, padding: "6px 9px", fontSize: 12, marginBottom: 8 }}>
          {err}
        </div>
      )}

      {!token ? (
        <button
          onClick={reveal}
          disabled={busy}
          style={{ background: "#185FA5", color: "#fff", border: "none", borderRadius: 7, padding: "9px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
        >
          {busy ? "Setting up…" : "Get my calendar link"}
        </button>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {/* Owner scope toggle — which jobs the main feed carries. The
              choice is part of the URL, so switching after subscribing
              means re-adding the calendar with the new link. */}
          {isOwner && (
            <div style={{ display: "flex", gap: 12, alignItems: "center", fontSize: 12, flexWrap: "wrap" }}>
              <span style={{ color: "#666", fontWeight: 600 }}>Sync:</span>
              {[
                { v: false, label: "All jobs" },
                { v: true, label: "Only jobs I'm assigned to" },
              ].map((o) => (
                <label key={String(o.v)} style={{ display: "flex", alignItems: "center", gap: 5, cursor: "pointer" }}>
                  <input type="radio" checked={mineOnly === o.v} onChange={() => { setMineOnly(o.v); setCopied(false); }} />
                  {o.label}
                </label>
              ))}
            </div>
          )}
          <a
            href={urls.webcal}
            style={{ display: "inline-block", textAlign: "center", background: "#185FA5", color: "#fff", borderRadius: 7, padding: "10px 14px", fontSize: 13, fontWeight: 700, textDecoration: "none" }}
          >
            📲 Add to my phone calendar
          </a>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button
              onClick={copy}
              style={{ background: "#f0f4fa", color: "#185FA5", border: "1px solid #c8dcee", borderRadius: 7, padding: "8px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
            >
              {copied ? "✓ Copied!" : "Copy link"}
            </button>
            <button
              onClick={reset}
              disabled={busy}
              style={{ background: "none", color: "#888", border: "1px solid #ddd", borderRadius: 7, padding: "8px 12px", fontSize: 12, cursor: "pointer" }}
            >
              Reset link
            </button>
          </div>
          <div style={{ fontSize: 11, color: "#888", lineHeight: 1.5 }}>
            <b>iPhone:</b> tap "Add to my phone calendar" → Subscribe.<br />
            <b>Google Calendar:</b> tap "Copy link", then on a computer go to Other calendars → From URL → paste.<br />
            Keep this link private — anyone with it can see your jobs. Use "Reset link" if it leaks.
            {isOwner && <><br />Changing the "Sync" choice changes the link — re-subscribe to apply it.</>}
          </div>

          {/* Per-guard feeds for color-coding. Calendar apps only color
              whole CALENDARS (per-event colors in subscribed feeds are
              ignored), so each guard gets their own feed: subscribe to
              each and pick its color in the calendar app. */}
          {isOwner && guards.length > 0 && (
            <div style={{ borderTop: "1px solid #f0f0f0", paddingTop: 10, marginTop: 2 }}>
              <button
                onClick={() => setShowGuardFeeds((v) => !v)}
                style={{ background: "none", border: "none", color: "#185FA5", fontSize: 12, fontWeight: 600, cursor: "pointer", padding: 0 }}
              >
                🎨 Color-code by guard {showGuardFeeds ? "▾" : "▸"}
              </button>
              {showGuardFeeds && (
                <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }}>
                  <div style={{ fontSize: 11, color: "#888", lineHeight: 1.5 }}>
                    Calendar apps can't color individual events from a subscribed feed — but they
                    color whole calendars. Subscribe to each guard's calendar below, then give each
                    one its own color in Google/Apple Calendar. A job with two guards shows on both.
                  </div>
                  {guards.map((g) => {
                    const gUrls = calendarSubscribeUrls(token, { guardId: g.id });
                    return (
                      <div key={g.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
                        <span style={{ flex: 1, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {g.name || g.email || "Guard"}
                        </span>
                        <a href={gUrls.webcal}
                          style={{ background: "#f0f4fa", color: "#185FA5", border: "1px solid #c8dcee", borderRadius: 6, padding: "5px 10px", fontSize: 11, fontWeight: 600, textDecoration: "none" }}>
                          📲 Add
                        </a>
                        <button onClick={() => copyGuardLink(g.id)}
                          style={{ background: "none", color: "#185FA5", border: "1px solid #c8dcee", borderRadius: 6, padding: "5px 10px", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
                          {copiedGuardId === g.id ? "✓ Copied" : "Copy link"}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
